import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    FormControl,
    Stepper,
    Step,
    StepLabel,
    Theme,
    StepIcon,
    StepIconProps,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GradeIcon from '@mui/icons-material/Grade';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import KenInput from '../../../../../GlobalComponents/KenInput';
import KenSelect from '../../../../../GlobalComponents/KenSelect';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'antd';
import { DROP_DOWN } from './Helper/DropDownHelper';
import { useLocation, useNavigate } from 'react-router-dom';
import { createForum, getForumDetails, updateForum } from '../../../../../Utils/apiService';
import KenLoader from '../../../../../GlobalComponents/KenLoader';
import KenSnackbar from '../../../../../GlobalComponents/KenSnackbar';
import routes from '../../../../../Utils/routes';
import { useParams } from 'react-router-dom';

// Tab definition interface
interface Tab {
    id: string;
    label: string;
    icon: React.ReactNode;
}

interface IForum {
    name: string;
    intro: string;
    type: string;
    duedate: any;
    maxattachments: number;
    forcesubscribe: number;
    trackingtype: number;
    lockdiscussionafter: number;
    blockperiod: number;
    blockafter: number;
    warnafter: number;
}

// Create styles using makeStyles
const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        height: 'calc(100vh - 160px)',
        gap: '12px',
    },
    leftPanel: {
        width: 280,
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: theme.spacing(3),
        paddingBottom: 0,
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
    },
    rightPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05)',
    },
    contentArea: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: 'auto',
    },
    buttonArea: {
        padding: theme.spacing(2, 4),
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: '#fff',
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
    },
    formControl: {
        marginBottom: theme.spacing(3),
    },
    inputLabel: {
        textAlign: 'left',
        display: 'block',
        marginBottom: theme.spacing(1),
        fontWeight: 500,
    },
    textField: {
        width: '100%',
    },
    select: {
        width: '100%',
    },
    textarea: {
        width: '100%',
        minHeight: 150,
    },
    cancelButton: {
        marginRight: theme.spacing(2),
    },
    saveButton: {
        backgroundColor: '#3986c8',
        '&:hover': {
            backgroundColor: '#2d6da3',
        },
    },
    stepperRoot: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
    },
    step: {
        '& .MuiStepLabel-root': {
            alignItems: 'flex-start',
        },
        '& .MuiStepIcon-root': {
            color: '#3986c8',
        },
        '& .MuiStepIcon-root.Mui-active': {
            color: '#3986c8',
        },
        '& .MuiStepLabel-label': {
            marginTop: 0,
            textAlign: 'left',
        },
    },
    activeStep: {
        '& .MuiStepIcon-root': {
            color: '#3986c8',
        },
    },
    completedStep: {
        '& .MuiStepIcon-root': {
            color: '#4caf50',
        },
        '& .MuiStepLabel-label': {
            color: '#4caf50',
        },
    },
    title: {
        marginBottom: theme.spacing(4),
    },
    requiredIndicator: {
        color: 'red',
    },
    tabsList: {
        padding: 0,
        flexGrow: 1,
        overflowY: 'auto',
        '& > .MuiListItem-root:last-child': {
            marginBottom: theme.spacing(1),
        },
    },
    tabItem: {
        paddingLeft: 0,
        paddingRight: 0,
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: '8px',
        },
    },
    tabItemActive: {
        backgroundColor: 'rgba(57, 134, 200, 0.1)',
        borderRadius: '8px',
    },
    tabIcon: {
        minWidth: '40px',
        color: '#666',
    },
    tabText: {
        '& .MuiTypography-root': {
            fontWeight: 400,
            fontSize: '1rem',
        },
    },
    activeTabText: {
        '& .MuiTypography-root': {
            fontWeight: 500,
            color: '#3986c8',
        },
    },
    activeTabIcon: {
        color: '#3986c8',
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: '500 !important',
        color: '#333',
        marginBottom: theme.spacing(3),
        marginTop: theme.spacing(6),
        display: 'flex',
        alignItems: 'center',
        '&:first-child': {
            marginTop: 0,
        },
    },
    sectionIcon: {
        marginRight: theme.spacing(1),
        color: '#3986c8',
    },
    sectionContent: {
        marginBottom: theme.spacing(4),
        background: "#F6F8Fb",
        padding: theme.spacing(3),
        borderRadius: "12px"
    },
    sectionDivider: {
        margin: theme.spacing(4, 0),
    },
    assessmentSettings: {
        background: '#F6F8FB'
    },
    font16_500: {
        fontSize: "16px",
        fontWeight: "500"
    },
    font14_400: {
        fontSize: "14px",
        fontWeight: "400"
    }
}));

const CustomStepIcon = (props: StepIconProps) => {
    const { active, completed } = props;

    return (
        <>
            {completed ? (
                <CheckCircleIcon style={{ color: '#4caf50' }} />
            ) : (
                <StepIcon {...props} />
            )}
        </>
    );
};

const steps = ['General', 'Assignment settings'];

const mainTabs: Tab[] = [
    { id: 'Availability', label: 'Availability', icon: <AccessTimeIcon /> },
    { id: 'ForumType', label: 'ForumType', icon: <GradeIcon /> },
    { id: 'Attachments', label: 'Attachments', icon: <QuestionAnswerIcon /> },
    { id: 'DiscussionLocking', label: 'Discussion Locking', icon: <LocalOfferIcon /> },
    { id: 'SubscriptionTracking', label: 'Subscription tracking', icon: <LocalOfferIcon /> },
    { id: 'ThresholdBlocking', label: 'Threshold Blocking', icon: <QuestionAnswerIcon /> },
];

const ForumForm: React.FC = () => {
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState<number[]>([]);
    const [activeTab, setActiveTab] = useState('Availability'); // Changed initial tab to match new ID
    const [sectionsInitialized, setSectionsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const location = useLocation();
    const id = location.state?.id;
    const [isEditMode, setIsEditMode] = useState(false);


    const [forumData, setForumData] = useState<IForum>({
        name: "",
        intro: "",
        type: "single",
        duedate: dayjs(),
        maxattachments: 0,
        forcesubscribe: 0,
        trackingtype: 1,
        lockdiscussionafter: 0,
        blockperiod: 0,
        blockafter: 0,
        warnafter: 0
    });

    const { title, courses, type, courseId, tab } = location.state || {
        title: '',
        courses: null,
        type: '',
        courseId: '',
        tab: ""
    };

    const navigate = useNavigate();
    const contentRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const { control, handleSubmit, reset } = useForm<IForum>();

    const onSubmit: SubmitHandler<IForum> = (data: IForum) => {
        const { name, intro } = data;
        if (!completedSteps.includes(activeStep)) {
            setCompletedSteps([...completedSteps, activeStep]);
        }
        setActiveStep(1);
        setActiveTab('Availability'); // Changed to match new tab ID
        setForumData({
            ...forumData,
            name,
            intro
        })
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setForumData({
            ...forumData,
            [name]: value
        })
    }

    const handleSave = async () => {
        try {
            setIsLoading(true);

            // Common forum settings for both create and update
            const forumSettings = {
                ...forumData,
                visible: 0,
                duedate: dayjs(forumData.duedate).unix()
            };

            let response;

            if (isEditMode) {
                // Update forum payload
                const updatePayload = {
                    method: "post",
                    forumsettings: [forumSettings],
                    forumid: String(id)
                };

                response = await updateForum(updatePayload);
            } else {
                // Create forum payload
                const createPayload = {
                    method: "post",
                    forumsettings: [forumSettings],
                    generalsection: 0,
                    courseoffering: courses.CourseOfferingID
                };

                response = await createForum(createPayload);
            }

            setIsLoading(false);
            console.log(courses?.Id, tab, "comehre")
            if (response.success || !response.hasOwnProperty('errorcode')) {
                showSnackbar(`Forum ${isEditMode ? 'updated' : 'created'} successfully`, "success");
                setTimeout(() => {
                    console.log("Navigating with Course ID:", courses?.Id || courseId, "Tab:", tab);
                    navigate(`${routes.courseElaborate}?courseid=${courses?.Id || courseId}&tab=${tab}`);
                    console.log("created")
                }, 1000);
            } else {
                showSnackbar(`Forum not ${isEditMode ? 'updated' : 'created'}`, "error");
            }
        } catch (error) {
            setIsLoading(false);
            console.log(`Error while ${isEditMode ? 'updating' : 'creating'} Forum`, error);
            showSnackbar(`Error ${isEditMode ? 'updating' : 'creating'} Forum`, "error");
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        console.log("Showing snackbar:", message, severity); // Debug log
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleTabClick = (tabId: string) => {
        setActiveTab(tabId);
        // Scroll to the corresponding section
        if (sectionRefs.current[tabId] && contentRef.current) {
            sectionRefs.current[tabId]?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Initialize sections
    useEffect(() => {
        if (activeStep === 1) {
            // Use a short timeout to ensure elements are in the DOM
            const timer = setTimeout(() => {
                setSectionsInitialized(true);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [activeStep]);

    // Update active tab based on scroll position
    useEffect(() => {
        if (activeStep !== 1 || !contentRef.current || !sectionsInitialized) return;

        const handleScroll = () => {
            if (!contentRef.current) return;

            const scrollContainer = contentRef.current;
            const scrollPosition = scrollContainer.scrollTop;
            const containerHeight = scrollContainer.clientHeight;
            const containerScrollHeight = scrollContainer.scrollHeight;

            // Calculate the distance from the bottom of the scroll container
            const distanceFromBottom = containerScrollHeight - (scrollPosition + containerHeight);

            // If we're near the bottom, activate the last tab
            if (distanceFromBottom < 50) {
                const lastTab = mainTabs[mainTabs.length - 1].id;
                if (activeTab !== lastTab) {
                    setActiveTab(lastTab);
                }
                return;
            }

            // Find which section is currently in view
            let currentTab = mainTabs[0].id;

            for (const tab of mainTabs) {
                const sectionElement = sectionRefs.current[tab.id];
                if (sectionElement) {
                    // Get element position relative to the viewport
                    const rect = sectionElement.getBoundingClientRect();
                    const sectionTop = rect.top;

                    // Add a small offset to improve detection
                    if (sectionTop <= 150) {
                        currentTab = tab.id;
                    }
                }
            }

            if (currentTab !== activeTab) {
                setActiveTab(currentTab);
            }
        };

        const contentElement = contentRef.current;
        contentElement.addEventListener('scroll', handleScroll);

        // Trigger initial scroll detection
        handleScroll();

        return () => {
            contentElement.removeEventListener('scroll', handleScroll);
        };
    }, [activeStep, activeTab, sectionsInitialized]);
    useEffect(() => {
        // Check if we have an ID parameter, indicating edit mode
        if (id) {
            setIsEditMode(true);
            fetchForumDetails(id);
        }
    }, [id]);
    const fetchForumDetails = async (forumId: string) => {
        try {
            setIsLoading(true);
            const payload = {
                method: "post",
                forumid: Number(forumId)
            };

            const response = await getForumDetails(payload);

            let data: { [key: string]: any } = {};
            if (response.data && Array.isArray(response.data)) {
                interface ForumDetailItem {
                    name: string;
                    value: any;
                }

                // Extract data from response
                (response.data as ForumDetailItem[]).forEach((item: ForumDetailItem) => {
                    data[item.name] = item.value;
                });

                console.log("Transformed forum data:", data);

                setForumData({
                    name: data.name || "",
                    intro: data.intro || "",
                    type: data.type || "single",
                    duedate: data.duedate ? dayjs.unix(Number(data.duedate)) : dayjs(),
                    maxattachments: Number(data.maxattachments) || 0,
                    forcesubscribe: Number(data.forcesubscribe) || 0,
                    trackingtype: Number(data.trackingtype) || 1,
                    lockdiscussionafter: Number(data.lockdiscussionafter) || 0,
                    blockperiod: Number(data.blockperiod) || 0,
                    blockafter: Number(data.blockafter) || 0,
                    warnafter: Number(data.warnafter) || 0
                });

                reset({
                    name: data.name || "",
                    intro: data.intro || "",
                    duedate: data.duedate ? dayjs.unix(Number(data.duedate)) : dayjs() || "",
                    type: data.type || "single",
                    maxattachments: Number(data.maxattachments) || 0,
                    forcesubscribe: Number(data.forcesubscribe) || 0,
                    trackingtype: Number(data.trackingtype) || 1,
                    lockdiscussionafter: Number(data.lockdiscussionafter) || 0,
                    blockperiod: Number(data.blockperiod) || 0,
                    blockafter: Number(data.blockafter) || 0,
                    warnafter: Number(data.warnafter) || 0
                });
            }

            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching forum details:", error);
            showSnackbar("Failed to load forum details", "error");
            setIsLoading(false);
        }
    };
    const handleCancelClick = () => {
        navigate(`${routes.academics}`);
    };
    return (
        <>
            <KenSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            />
            {isLoading &&
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <KenLoader />
                </Box>
            }
            <Box className={classes.root}>
                <Paper className={classes.leftPanel} elevation={0}>
                    <Stepper activeStep={activeStep} orientation="vertical" className={classes.stepperRoot}>
                        {steps.map((label, index) => (
                            <Step
                                key={label}
                                className={`
                                ${classes.step}
                                ${index === activeStep ? classes.activeStep : ''}
                                ${completedSteps.includes(index) ? classes.completedStep : ''}
                            `}
                                completed={completedSteps.includes(index)}
                            >
                                <StepLabel StepIconComponent={CustomStepIcon}>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {activeStep === 1 && (
                        <>
                            {/* Main tabs */}
                            <List className={classes.tabsList}>
                                {mainTabs.map((tab) => (
                                    <ListItem
                                        key={tab.id}
                                        button
                                        className={`${classes.tabItem} ${activeTab === tab.id ? classes.tabItemActive : ''}`}
                                        onClick={() => handleTabClick(tab.id)}
                                    >
                                        <ListItemIcon className={`${classes.tabIcon} ${activeTab === tab.id ? classes.activeTabIcon : ''}`}>
                                            {tab.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={tab.label}
                                            className={`${classes.tabText} ${activeTab === tab.id ? classes.activeTabText : ''}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </Paper>

                <Paper className={classes.rightPanel} elevation={0}>
                    <Box className={classes.contentArea} ref={contentRef}>
                        {activeStep === 0 ? (
                            <form id="assessment-form" onSubmit={handleSubmit(onSubmit)}>
                                <Box className={classes.formControl}>
                                    <Typography className={classes.inputLabel}>Forum name</Typography>
                                    <Controller
                                        name="name"
                                        control={control}
                                        defaultValue=""
                                        rules={{ required: "Forum name is required" }}
                                        render={({ field, fieldState }) => (
                                            <KenInput
                                                {...field}
                                                placeholder="Enter assignment"
                                                error={fieldState.error?.message}
                                                errorMessage={true}
                                            />
                                        )}
                                    />
                                </Box>

                                {/* <Box display="flex" gap={2} className={classes.formControl}>
                                <Box flex={1}>
                                    <Typography className={classes.inputLabel}>Select class</Typography>
                                    <Controller
                                        name="class"
                                        control={control}
                                        defaultValue=""
                                        render={({ field, fieldState }) => (
                                            <KenSelect
                                                {...field}
                                                selectLabel="Select"
                                                options={classOptions}
                                                error={fieldState.error?.message}
                                                errorMessage={true}
                                            />
                                        )}
                                    />
                                </Box>

                                <Box flex={1}>
                                    <Typography className={classes.inputLabel}>Select subject</Typography>
                                    <Controller
                                        name="program"
                                        control={control}
                                        defaultValue=""
                                        render={({ field, fieldState }) => (
                                            <KenSelect
                                                {...field}
                                                selectLabel="Select"
                                                options={subjectOptions}
                                                error={fieldState.error?.message}
                                                errorMessage={true}
                                            />
                                        )}
                                    />
                                </Box>

                                <Box flex={1}>
                                    <Typography className={classes.inputLabel}>Select section</Typography>
                                    <Controller
                                        name="section"
                                        control={control}
                                        defaultValue=""
                                        render={({ field, fieldState }) => (
                                            <KenSelect
                                                {...field}
                                                selectLabel="Select"
                                                options={sectionOptions}
                                                error={fieldState.error?.message}
                                                errorMessage={true}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box> */}

                                <Box className={classes.formControl}>
                                    <Typography className={classes.inputLabel}>Forum Description</Typography>
                                    <Controller
                                        name="intro"
                                        control={control}
                                        defaultValue=""
                                        render={({ field, fieldState }) => (
                                            <KenInput
                                                {...field}
                                                placeholder="Enter text"
                                                multiline
                                                rows={6}
                                                className={classes.textarea}
                                                error={fieldState.error?.message}
                                                errorMessage={true}
                                            />
                                        )}
                                    />
                                </Box>
                            </form>
                        ) : (
                            <div>
                                {/* Availability Settings Section */}
                                <div
                                    ref={(el) => sectionRefs.current['Availability'] = el}
                                    id="Availability-section"
                                >
                                    <Typography className={classes.sectionTitle}>
                                        Availability Settings
                                    </Typography>
                                    <Box className={classes.sectionContent} textAlign={'start'}>
                                        <FormControl fullWidth className={classes.formControl}>
                                            <Box sx={{ p: 2, background: "#F8FAFD", borderRadius: "8px" }}>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} sm={4}>
                                                        <Typography sx={{ fontSize: "14px", fontWeight: 500, mb: 1 }}>
                                                            Due Date & Time <span style={{ color: "red" }}>*</span>
                                                        </Typography>
                                                        <DatePicker
                                                            value={forumData.duedate}
                                                            onChange={(date) => setForumData({ ...forumData, duedate: date })}
                                                            showTime={{ format: "hh:mm A" }}
                                                            format="DD-MM-YYYY hh:mm A"
                                                            placeholder="Select date & time"
                                                            style={{ width: "100%", height: "48px" }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </FormControl>
                                    </Box>
                                    <Divider className={classes.sectionDivider} />
                                </div>

                                {/* Forum Type Section */}
                                <div
                                    ref={(el) => sectionRefs.current['ForumType'] = el}
                                    id="ForumType-section"
                                >
                                    <Typography className={classes.sectionTitle}>
                                        Forum Type
                                    </Typography>
                                    <Box className={classes.sectionContent} textAlign={'start'}>
                                        <FormControl className={classes.formControl} sx={{ width: "30%" }}>
                                            <KenSelect
                                                label="Forum Type"
                                                selectLabel="Select"
                                                name="type"
                                                value={forumData.type}
                                                handleChange={handleChange}
                                                options={DROP_DOWN.forumTypes}
                                                required
                                            />
                                        </FormControl>
                                    </Box>
                                    <Divider className={classes.sectionDivider} />
                                </div>

                                {/* Attachments Section */}
                                <div
                                    ref={(el) => sectionRefs.current['Attachments'] = el}
                                    id="Attachments-section"
                                >
                                    <Typography className={classes.sectionTitle}>
                                        Attachments
                                    </Typography>
                                    <Box className={classes.sectionContent} textAlign={'start'}>
                                        <FormControl className={classes.formControl} sx={{ width: "30%" }}>
                                            <KenSelect
                                                label="Maximum  number of attachmements"
                                                selectLabel="Select"
                                                name="maxattachments"
                                                value={forumData.maxattachments}
                                                handleChange={handleChange}
                                                options={DROP_DOWN.maxAttachments}
                                                required
                                            />
                                        </FormControl>
                                    </Box>
                                    <Divider className={classes.sectionDivider} />
                                </div>
                                {/* Subscription Tracking */}
                                <div
                                    ref={(el) => sectionRefs.current['SubscriptionTracking'] = el}
                                    id="SubscriptionTracking-section"
                                >
                                    <Typography className={classes.sectionTitle}>
                                        Subscription Tracking
                                    </Typography>
                                    <Box className={classes.sectionContent} textAlign="start">
                                        <Grid container direction="column" spacing={2}>
                                            <Grid item>
                                                <FormControl className={classes.formControl} sx={{ width: "30%" }}>
                                                    <KenSelect
                                                        label="Subscription Mode"
                                                        selectLabel="Select"
                                                        name="forcesubscribe"
                                                        value={forumData.forcesubscribe}
                                                        handleChange={handleChange}
                                                        options={DROP_DOWN.subscriptionMode}
                                                        required
                                                    />
                                                </FormControl>
                                            </Grid>
                                            <Grid item>
                                                <FormControl className={classes.formControl} sx={{ width: "30%" }}>
                                                    <KenSelect
                                                        label="Read Tracking"
                                                        selectLabel="Select"
                                                        name="trackingtype"
                                                        value={forumData.trackingtype}
                                                        handleChange={handleChange}
                                                        options={DROP_DOWN.readTracking}
                                                        required
                                                    />
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    <Divider className={classes.sectionDivider} />
                                </div>

                                {/* Threshold Blocking Section */}
                                <div
                                    ref={(el) => sectionRefs.current['ThresholdBlocking'] = el}
                                    id="ThresholdBlocking-section"
                                >
                                    <Typography className={classes.sectionTitle}>
                                        Threshold Blocking
                                    </Typography>
                                    <Box className={classes.sectionContent} textAlign={'start'}>
                                        <FormControl className={classes.formControl} sx={{ width: "30%" }}>
                                            <KenSelect
                                                label="Lock discussions after period of inactivity"
                                                selectLabel="Select"
                                                name="lockdiscussionafter"
                                                value={forumData.lockdiscussionafter}
                                                handleChange={handleChange}
                                                options={DROP_DOWN.discussionLocking}
                                                required
                                            />
                                        </FormControl>
                                    </Box>
                                    <Divider className={classes.sectionDivider} />
                                </div>
                                {/* Discussion Locking Section */}
                                <div
                                    ref={(el) => sectionRefs.current['DiscussionLocking'] = el}
                                    id="DiscussionLocking-section"
                                >
                                    <Typography className={classes.sectionTitle}>
                                        Discussion Locking
                                    </Typography>
                                    <Box className={classes.sectionContent}>
                                        <FormControl fullWidth className={classes.formControl}>
                                            <Box sx={{ backgroundColor: "#F8FAFC", padding: 2, borderRadius: 2 }}>
                                                <Grid container spacing={2}>
                                                    {/* Time Period for Blocking */}
                                                    <Grid item xs={4}>
                                                        <KenSelect
                                                            label="Time Period for Blocking"
                                                            name="blockperiod"
                                                            value={forumData.blockperiod}
                                                            onChange={handleChange}
                                                            options={DROP_DOWN.blockingTimePeriod}
                                                            fullWidth
                                                        />
                                                    </Grid>

                                                    {/* Post threshold for blocking */}
                                                    <Grid item xs={4}>
                                                        <KenInput
                                                            label="Post threshold for blocking"
                                                            name="blockafter"
                                                            value={forumData.blockafter}
                                                            onChange={handleChange}
                                                            fullWidth
                                                            required
                                                        />
                                                    </Grid>

                                                    {/* Post threshold for warning */}
                                                    <Grid item xs={4}>
                                                        <KenInput
                                                            label="Post threshold for warning"
                                                            name="warnafter"
                                                            value={forumData.warnafter}
                                                            onChange={handleChange}
                                                            fullWidth
                                                            required
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </FormControl>
                                    </Box>
                                </div>
                            </div>
                        )}
                    </Box>

                    {/* Bottom fixed button area */}
                    <Box className={classes.buttonArea}>
                        {activeStep === 1 && (
                            <Button
                                variant="text"
                                onClick={() => setActiveStep(0)}
                                style={{ marginRight: 'auto' }}
                            >
                                Back
                            </Button>
                        )}
                        <Button
                            variant="text"
                            className={classes.cancelButton}
                            onClick={handleCancelClick}
                        >
                            Cancel
                        </Button>
                        {activeStep === 0 ? (
                            <Button
                                variant="contained"
                                color="primary"
                                className={classes.saveButton}
                                type="submit"
                                form="assessment-form"
                            >
                                Save & proceed to Forum settings
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSave}
                                className={classes.saveButton}
                            >
                                Save settings
                            </Button>
                        )}
                    </Box>
                </Paper >
            </Box >
        </>
    );
};

export default ForumForm;